import { createStyles, withStyles, AlertDialog, Typography } from '@paintscout/ui'
import type { WithStyles } from '@paintscout/ui'
import { useDialogs } from '@paintscout/ui-business'
import React, { useEffect } from 'react'
import { useAuth } from 'react-auth0'
import { useLocation, type RouteComponentProps } from 'react-router-dom'
import { clearUserData, clearClientStorage } from 'shared/util/auth'
import { storeBranchReturnUrl } from 'shared/util/routing'
import { track } from 'shared/util/analytics'

export interface LogoutProps extends WithStyles<typeof styles>, RouteComponentProps {}

const styles = createStyles({
  root: {
    height: '50vh',
    color: '#757575',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  spinner: {
    marginBottom: 40
  }
})

function Logout(props: LogoutProps) {
  const { classes } = props
  const { logout, session } = useAuth()
  const { openDialog, dismissDialog } = useDialogs()
  const location = useLocation<{ message?: string }>()

  useEffect(() => {
    track('user_logged_out', { reason: location.state?.message ?? 'manual' })
    document.title = 'PaintScout | Logout'

    // Clear localStorage, then wipe the offline IndexedDB stores / caches (time-boxed so a hung
    // WebKit IDB can't block), then suppress any unsaved-changes prompt before the Auth0 redirect.
    const clearAndLogout = async (logoutOptions?: Parameters<typeof logout>[0]) => {
      await clearUserData(session?.app_metadata?.companyId)
      await clearClientStorage()
      // Auth0 logout can only return to the canonical origin — stash the
      // branch-deploy prefix so the next login redirects back to the branch
      storeBranchReturnUrl()
      window.onbeforeunload = () => null
      logout(logoutOptions)
    }

    if (location.state && location.state.message) {
      openDialog(AlertDialog, {
        title: 'Logging Out',
        message: location.state.message,
        onConfirm: () => {
          dismissDialog()
          void clearAndLogout()
        }
      })
    } else {
      void clearAndLogout({ logoutParams: { returnTo: process.env.PS_APP_URL as string } })
    }
  }, [])

  return (
    <div className={classes.root}>
      <Typography variant="h5">Logging out...</Typography>
    </div>
  )
}

export default withStyles(styles)(Logout)
